/** Material UI Packages. */
import { GridActionsCellItem } from '@mui/x-data-grid';

/** Material UI Packages. */
import { Typography, Link } from '@mui/material';

/** Material UI Icons. */
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import FileUploadIcon from '@mui/icons-material/FileUpload';

/** Internal Components. */
import { proof_status, delivery_order_status, customs_status, customs_hold_status } from 'shared/config/constantsData';
import {
    CustomSelectCell_string,
    CustomAutocompleteCell_string,
    CustomAutocompleteCell_TradePartner,
    FillDownHeader,
    DateTypingEditCell,
} from 'shared/ui/RenderCell';
import { customized_style } from 'shared/config/Styles';
import { createMultiHeaderGroup } from 'shared/ui/Tooltip';
import { getClassName } from 'shared/lib/todoColorHandler';
import GoogleRenderCell from 'shared/ui/GoogleRenderCell';
import { parseDateToLocal } from 'shared/lib/dateFormatter';
import {
    createOptionPasteParser,
    createTradePartnerValueParser,
    tradePartnerValueFormatter,
    parsePastedDate,
} from 'shared/lib/datagridHelpers';

export const bolCol = (
    handleDeleteClick,
    presetData,
    unitOptions,
    handleUnitChange,
    tradePartnerOptions,
    handleTradePartnerChange,
    matchedTodoContainer,
    handleLinkClick,
    handleFillDown,
    onInvalidOption,
) => {
    const deliveryOrder = presetData?.delivery_order;
    const proof = presetData?.proof;
    const customs = presetData?.customs;

    return [
        {
            field: 'id',
            headerName: 'ID',
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            hide: true,
        },

        {
            field: 'alias',
            headerName: 'Alias',
            flex: 0.8,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => <FillDownHeader label="Alias" field="alias" onFillDown={handleFillDown} />,
        },

        {
            field: 'quantity',
            headerName: 'Vol',
            flex: 0.4,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            type: 'number',
            renderHeader: () => <FillDownHeader label="Vol" field="quantity" onFillDown={handleFillDown} />,
        },
        {
            field: 'il_opus_volume_unit_id',
            headerName: 'Unit',
            flex: 0.8,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => (
                <FillDownHeader label="Unit" field="il_opus_volume_unit_id" onFillDown={handleFillDown} />
            ),
            renderEditCell: (params) => (
                <CustomAutocompleteCell_string
                    params={params}
                    options={unitOptions}
                    handleInputChange={(event, value) => handleUnitChange(event, value)}
                    shouldFocus={true}
                />
            ),
        },
        {
            field: 'hazmat',
            headerName: 'Hazmat',
            flex: 0.5,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            type: 'boolean',
            renderHeader: () => <FillDownHeader label="Hazmat" field="hazmat" onFillDown={handleFillDown} />,
            valueGetter: (value) => value === true,
        },
        {
            field: 'weight',
            headerName: 'Weight',
            flex: 0.6,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            type: 'number',
            renderHeader: () => <FillDownHeader label="Weight" field="weight" onFillDown={handleFillDown} />,
            renderCell: (params) => `${params.value || 0} lb`,
        },
        {
            field: 'reference',
            headerName: 'Reference',
            flex: 1.2,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => <FillDownHeader label="Reference" field="reference" onFillDown={handleFillDown} />,
            renderCell: (params) => (
                <Typography sx={customized_style.itemDatagrid_Typography}>{params.value}</Typography>
            ),
        },
        {
            field: 'il_location_id_pick_up',
            headerName: 'Pick Up',
            flex: 1.2,
            editable: deliveryOrder,
            align: 'center',
            headerAlign: 'center',
            headerClassName: deliveryOrder ? '' : 'disabled-header',
            renderHeader: () => (
                <FillDownHeader label="Pick Up" field="il_location_id_pick_up" onFillDown={handleFillDown} />
            ),
            cellClassName: (params) => {
                const filtered_todo = matchedTodoContainer.filter((item) => item.filter_id === params.row.id);
                return getClassName(filtered_todo, 'delivery_order', 'header-cell');
            },
            renderCell: (params) => (
                <Typography
                    sx={{ ...customized_style.itemDatagrid_Typography, color: deliveryOrder ? 'inherit' : '#C7C7C7' }}
                >
                    {params.value}
                </Typography>
            ),
            renderEditCell: (params) => (
                <GoogleRenderCell
                    params={params}
                    shouldFocus={true}
                    reducer={'setBolUpdateData'}
                    tableName={'il_ftl_ltl_alias'}
                    hasRowUpdate={false}
                    tableId={[params.row.il_ftl_ltl_id]}
                />
            ),
        },
        {
            field: 'il_location_id_deliver_to',
            headerName: 'Delivery To',
            flex: 1.2,
            editable: deliveryOrder,
            align: 'center',
            headerAlign: 'center',
            headerClassName: deliveryOrder ? '' : 'disabled-header',
            renderHeader: () => (
                <FillDownHeader label="Delivery To" field="il_location_id_deliver_to" onFillDown={handleFillDown} />
            ),
            cellClassName: (params) => {
                const filtered_todo = matchedTodoContainer.filter((item) => item.filter_id === params.row.id);
                return getClassName(filtered_todo, 'delivery_order', 'header-cell');
            },
            renderCell: (params) => (
                <Typography
                    sx={{ ...customized_style.itemDatagrid_Typography, color: deliveryOrder ? 'inherit' : '#C7C7C7' }}
                >
                    {params.value}
                </Typography>
            ),
            renderEditCell: (params) => {
                return (
                    <GoogleRenderCell
                        params={params}
                        shouldFocus={true}
                        reducer={'setBolUpdateData'}
                        tableName={'il_ftl_ltl_alias'}
                        hasRowUpdate={false}
                        tableId={[params.row.il_ftl_ltl_id]}
                    />
                );
            },
        },
        {
            field: 'il_trade_partner_id',
            headerName: 'Trucker Name',
            flex: 1.2,
            editable: deliveryOrder,
            align: 'center',
            headerAlign: 'center',
            headerClassName: deliveryOrder ? '' : 'disabled-header',
            renderHeader: () => (
                <FillDownHeader label="Trucker Name" field="il_trade_partner_id" onFillDown={handleFillDown} />
            ),
            valueParser: createTradePartnerValueParser('il_trade_partner_id', tradePartnerOptions),
            valueFormatter: tradePartnerValueFormatter,
            cellClassName: (params) => {
                const filtered_todo = matchedTodoContainer.filter((item) => item.filter_id === params.row.id);
                return getClassName(filtered_todo, 'delivery_order', 'header-cell');
            },
            renderCell: (params) => {
                return (
                    <Typography
                        sx={{
                            ...customized_style.itemDatagrid_Typography,
                            color: deliveryOrder ? 'inherit' : '#C7C7C7',
                        }}
                    >
                        {params.value?.name}
                    </Typography>
                );
            },
            renderEditCell: (params) => {
                return (
                    <CustomAutocompleteCell_TradePartner
                        params={params}
                        options={tradePartnerOptions}
                        handleInputChange={handleTradePartnerChange}
                    />
                );
            },
        },
        {
            field: 'delivery_order',
            headerName: 'Status',
            flex: 0.8,
            editable: deliveryOrder,
            align: 'center',
            headerAlign: 'center',
            type: 'singleSelect',
            headerClassName: deliveryOrder ? '' : 'disabled-header',
            renderHeader: () => <FillDownHeader label="Status" field="delivery_order" onFillDown={handleFillDown} />,
            pastedValueParser: createOptionPasteParser(delivery_order_status, onInvalidOption),
            cellClassName: (params) => {
                const filtered_todo = matchedTodoContainer.filter((item) => item.filter_id === params.row.id);
                return getClassName(filtered_todo, 'delivery_order', 'header-cell');
            },
            renderCell: (params) => {
                return <span style={{ color: deliveryOrder ? 'inherit' : '#C7C7C7' }}>{params.value}</span>;
            },
            renderEditCell: (params) => <CustomSelectCell_string params={params} options={delivery_order_status} />,
        },
        {
            field: 'customs',
            headerName: 'Status',
            flex: 0.8,
            editable: customs,
            align: 'center',
            headerAlign: 'center',
            type: 'singleSelect',
            headerClassName: customs ? '' : 'disabled-header',
            renderHeader: () => <FillDownHeader label="Status" field="customs" onFillDown={handleFillDown} />,
            pastedValueParser: createOptionPasteParser(customs_status, onInvalidOption),
            cellClassName: (params) => {
                const fitlered_todo = matchedTodoContainer.filter((item) => item.filter_id === params.row.id);
                return getClassName(fitlered_todo, ['customs', 'customs_hold'], 'header-cell');
            },
            renderCell: (params) => {
                return <span style={{ color: customs ? 'inherit' : '#C7C7C7' }}>{params.value}</span>;
            },
            renderEditCell: (params) => <CustomSelectCell_string params={params} options={customs_status} />,
        },
        {
            field: 'customs_hold',
            headerName: 'Hold/Release',
            flex: 1,
            editable: customs,
            align: 'center',
            headerAlign: 'center',
            type: 'singleSelect',
            headerClassName: customs ? '' : 'disabled-header',
            renderHeader: () => (
                <FillDownHeader label="Hold/Release" field="customs_hold" onFillDown={handleFillDown} />
            ),
            pastedValueParser: createOptionPasteParser(customs_hold_status, onInvalidOption),
            cellClassName: (params) => {
                const filtered_todo = matchedTodoContainer.filter((item) => item.filter_id === params.row.id);
                return getClassName(filtered_todo, ['customs', 'customs_hold'], 'header-cell');
            },
            renderCell: (params) => {
                return <span style={{ color: customs ? 'inherit' : '#C7C7C7' }}>{params.value}</span>;
            },
            renderEditCell: (params) => <CustomSelectCell_string params={params} options={customs_hold_status} />,
        },
        {
            field: 'customs_other',
            headerName: 'Others',
            flex: 1,
            editable: customs,
            align: 'center',
            headerAlign: 'center',
            headerClassName: customs ? '' : 'disabled-header',
            renderHeader: () => <FillDownHeader label="Others" field="customs_other" onFillDown={handleFillDown} />,
            cellClassName: (params) => {
                const filtered_todo = matchedTodoContainer.filter((item) => item.filter_id === params.row.id);
                return getClassName(filtered_todo, ['customs', 'customs_hold'], 'header-cell');
            },
            renderCell: (params) => {
                return <span style={{ color: !customs ? '#C7C7C7' : 'inherit' }}>{params.value}</span>;
            },
        },
        {
            field: 'proof',
            headerName: 'Status',
            flex: 0.8,
            editable: proof,
            align: 'center',
            headerAlign: 'center',
            type: 'singleSelect',
            headerClassName: proof ? '' : 'disabled-header',
            renderHeader: () => <FillDownHeader label="Status" field="proof" onFillDown={handleFillDown} />,
            pastedValueParser: createOptionPasteParser(proof_status, onInvalidOption),
            cellClassName: (params) => {
                const filtered_todo = matchedTodoContainer.filter((item) => item.filter_id === params.row.id);
                return getClassName(filtered_todo, 'proof', 'header-cell');
            },
            renderCell: (params) => {
                return <span style={{ color: proof ? 'inherit' : '#C7C7C7' }}>{params.value}</span>;
            },
            renderEditCell: (params) => <CustomSelectCell_string params={params} options={proof_status} />,
        },
        {
            field: 'proof_date',
            headerName: 'Date',
            type: 'date',
            pastedValueParser: parsePastedDate,
            flex: 0.7,
            editable: proof,
            align: 'center',
            headerAlign: 'center',
            headerClassName: proof ? '' : 'disabled-header',
            renderHeader: () => <FillDownHeader label="Date" field="proof_date" onFillDown={handleFillDown} />,
            cellClassName: (params) => {
                const filtered_todo = matchedTodoContainer.filter((item) => item.filter_id === params.row.id);
                return getClassName(filtered_todo, 'proof', 'header-cell');
            },
            valueGetter: (params) => {
                return parseDateToLocal(params);
            },
            renderCell: (params) => {
                const date = parseDateToLocal(params.value);
                const formattedDate = date
                    ? date.toLocaleDateString('en-US', {
                          year: '2-digit',
                          month: '2-digit',
                          day: '2-digit',
                      })
                    : '';

                return (
                    <Typography
                        sx={{
                            ...customized_style.itemDatagrid_Typography,
                            color: proof ? 'inherit' : '#C7C7C7',
                        }}
                    >
                        {formattedDate}
                    </Typography>
                );
            },
            renderEditCell: (params) => <DateTypingEditCell params={params} />,
        },

        {
            field: 'actions',
            headerName: '',
            flex: 0.5,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            type: 'actions',
            getActions: (params) => {
                const needsCustoms = presetData?.customs && !params.row.customs_link;
                const needsDeliveryOrder = presetData?.delivery_order && !params.row.delivery_order_link;
                const needsProof = presetData?.proof && !params.row.proof_link;
                const shouldInput = needsCustoms || needsDeliveryOrder || needsProof;

                return [
                    <GridActionsCellItem
                        icon={<FileUploadIcon sx={{ color: shouldInput ? '#f48704' : 'rgba(0, 0, 0, 0.54)' }} />}
                        label="Link"
                        onClick={() => handleLinkClick(params.row)}
                        color="inherit"
                    />,
                    <GridActionsCellItem
                        icon={<DeleteIcon sx={{ color: 'rgba(0, 0, 0, 0.54)' }} />}
                        label="Delete"
                        onClick={() => handleDeleteClick(params.id)}
                        color="inherit"
                    />,
                ];
            },
        },
    ];
};

export const linkCols = (presetData, matchedTodoContainer) => {
    const customs = presetData?.customs;
    const deliveryOrder = presetData?.delivery_order;
    const proof = presetData?.proof;

    return [
        {
            field: 'id',
            headerName: 'ID',
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            sortable: false,
            hideable: false,
            hide: true,
            pinnable: false,
        },
        {
            field: 'delivery_order_link',
            headerName: 'Delivery Order Link',
            flex: 0.8,
            editable: deliveryOrder,
            align: 'center',
            headerAlign: 'center',
            headerClassName: deliveryOrder ? '' : 'disabled-header',
            cellClassName: (params) => {
                const fitlered_todo = matchedTodoContainer.filter((item) => item.filter_id === params.row.id);
                return getClassName(fitlered_todo, 'delivery_order', 'header-cell');
            },
            renderCell: (params) => {
                const linkStyle = !deliveryOrder ? { color: '#C7C7C7' } : {};
                const handleClick = (event) => {
                    if (!deliveryOrder) {
                        event.preventDefault();
                    }
                };
                return (
                    <Link
                        href={params.value ? `${params.value}` : '#'}
                        target="_blank"
                        rel="noopener"
                        style={linkStyle}
                        onClick={handleClick}
                    >
                        {params.value ? 'View File' : ''}
                    </Link>
                );
            },
        },
        {
            field: 'customs_link',
            headerName: 'Customs Link',
            flex: 0.8,
            editable: customs,
            align: 'center',
            headerAlign: 'center',
            headerClassName: customs ? '' : 'disabled-header',
            cellClassName: (params) => {
                const fitlered_todo = matchedTodoContainer.filter((item) => item.filter_id === params.row.id);
                return getClassName(fitlered_todo, 'customs', 'header-cell');
            },
            renderCell: (params) => {
                const linkStyle = !customs ? { color: '#C7C7C7' } : {};
                const handleClick = (event) => {
                    if (!customs) {
                        event.preventDefault();
                    }
                };
                return (
                    <Link
                        href={params.value ? `${params.value}` : '#'}
                        target="_blank"
                        rel="noopener"
                        style={linkStyle}
                        onClick={handleClick}
                    >
                        {params.value ? 'View File' : ''}
                    </Link>
                );
            },
        },
        {
            field: 'proof_link',
            headerName: 'Proof Link',
            flex: 0.8,
            editable: proof,
            align: 'center',
            headerAlign: 'center',
            headerClassName: proof ? '' : 'disabled-header',
            cellClassName: (params) => {
                const fitlered_todo = matchedTodoContainer.filter((item) => item.filter_id === params.row.id);
                return getClassName(fitlered_todo, 'proof', 'header-cell');
            },
            renderCell: (params) => {
                const linkStyle = !proof ? { color: '#C7C7C7' } : {};
                const handleClick = (event) => {
                    if (!proof) {
                        event.preventDefault();
                    }
                };
                return (
                    <Link
                        href={params.value ? `${params.value}` : '#'}
                        target="_blank"
                        rel="noopener"
                        style={linkStyle}
                        onClick={handleClick}
                    >
                        {params.value ? 'View File' : ''}
                    </Link>
                );
            },
        },
    ];
};

export const bolGroupingModel = (presetData, tabValue, historyData, tab) => {
    const deliveryOrder = presetData?.delivery_order;
    const proof = presetData?.proof;
    const customs = presetData?.customs;

    return [
        {
            groupId: 'FTL/LTL Cargo',
            headerAlign: 'center',
            children: [
                { field: 'alias' },
                { field: 'quantity' },
                { field: 'il_opus_volume_unit_id' },
                { field: 'hazmat' },
                { field: 'weight' },
                { field: 'reference' },
            ],
            renderHeaderGroup: (params) =>
                createMultiHeaderGroup(params, historyData, tabValue, tab, [
                    'alias',
                    'quantity',
                    'il_opus_volume_unit_id',
                    'hazmat',
                    'weight',
                    'reference',
                ]),
        },
        {
            groupId: 'Delivery Order',
            headerAlign: 'center',
            children: [
                { field: 'il_location_id_pick_up' },
                { field: 'il_location_id_deliver_to' },
                { field: 'il_trade_partner_id' },
                { field: 'delivery_order' },
                { field: 'delivery_order_link' },
            ],
            headerClassName: deliveryOrder ? '' : 'disabled-header',
            renderHeaderGroup: (params) =>
                createMultiHeaderGroup(params, historyData, tabValue, tab, [
                    'il_location_id_pick_up',
                    'il_location_id_deliver_to',
                    'il_trade_partner_id',
                    'delivery_order',
                    'delivery_order_link',
                ]),
        },
        {
            groupId: 'Customs',
            headerAlign: 'center',
            children: [
                { field: 'customs' },
                { field: 'customs_hold' },
                { field: 'customs_link' },
                { field: 'customs_other' },
            ],
            headerClassName: customs ? '' : 'disabled-header',
            renderHeaderGroup: (params) =>
                createMultiHeaderGroup(params, historyData, tabValue, tab, [
                    'customs',
                    'customs_date',
                    'customs_note',
                    'customs_link',
                ]),
        },
        {
            groupId: 'Proof',
            headerAlign: 'center',
            children: [{ field: 'proof' }, { field: 'proof_link' }, { field: 'proof_date' }],
            headerClassName: proof ? '' : 'disabled-header',
            renderHeaderGroup: (params) =>
                createMultiHeaderGroup(params, historyData, tabValue, tab, ['proof', 'proof_date', 'proof_link']),
        },
    ];
};
